import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useAdminData, type AdminProduct } from "./admin/AdminDataContext";
import { useLanguage } from "./LanguageContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";

const RECENTLY_VIEWED_KEY = "moecha_recently_viewed";

export function addToRecentlyViewed(productId: number) {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    let ids: number[] = raw ? JSON.parse(raw) : [];
    ids = [productId, ...ids.filter((id) => id !== productId)].slice(0, 6);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(ids));
  } catch (e) {
    console.warn("[MOECHA] Failed to update recently viewed:", e);
  }
}

export function RecentlyViewedSection({ currentProductId }: { currentProductId: number }) {
  const { products } = useAdminData();
  const { t, lang } = useLanguage();
  const theme = useTheme();
  const [recentProducts, setRecentProducts] = useState<AdminProduct[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      const ids: number[] = raw ? JSON.parse(raw) : [];
      const filtered = ids
        .filter((id) => id !== currentProductId)
        .map((id) => products.find((p) => p.id === id))
        .filter(Boolean) as AdminProduct[];
      setRecentProducts(filtered.slice(0, 4));
    } catch {
      setRecentProducts([]);
    }
  }, [currentProductId, products]);

  if (recentProducts.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 mt-24">
      <div className="text-center mb-12">
        <p
          className="tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
        >
          {t("detail.recently_viewed")}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto">
        {recentProducts.map((p) => (
          <Link key={p.id} to={`/product/${p.slug}`} className="group">
            <div className="overflow-hidden rounded-xl mb-3" style={{ backgroundColor: `${theme.border}40` }}>
              <ImageWithFallback
                src={p.images[0]}
                alt={p.name}
                className="w-full aspect-square object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
            </div>
            <h4
              className="tracking-[0.04em] transition-colors"
              style={{ fontFamily: theme.fontHeading, fontSize: "15px", fontWeight: 500, color: theme.fg }}
            >
              {localized(p, "name", lang)}
            </h4>
            <p
              style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: theme.muted }}
            >
              &euro;{p.price}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}